import { useState, useEffect } from "react";
import { Timestamp } from "firebase/firestore";
import { getLocalTimeZone, today, CalendarDate } from "@internationalized/date";
import { DateTime } from "luxon";

// Local
import { DbRead } from "../../database";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { ExistingMembership, MembershipStatus } from "../../models/membership";
import { ExistingCustomer } from "../../models/customer";
import {
  getFallbackMembershipConfig,
  getMembershipConfig,
  MembershipConfig,
} from "../../utils/templateAndMembershipUtils";
import { logger } from "../../logging";
import { useMembershipTemplatesStore } from "../../store/membership-templates";
import { phoneUtils } from "../../utils";
import currencyFormatter from "../../currency";
import { ButtonViewCustomer } from "../ButtonViewCustomer";
import { StyledTooltip } from "../StyledTooltip";
import { ButtonViewMembership } from "../ButtonViewMembership";
import { ExistingMembershipTemplate } from "../../models/membership-template";

type RenewalItem = {
  membershipConfig: MembershipConfig;
  customerID: string;
  customerName: string;
  customerContactDisplay: string;
  templateTitle: string;
  endDate: Timestamp;
};

type GroupedRenewals = {
  [date: string]: RenewalItem[];
};

type ViewMode = "upcoming" | "past";

/**
 * Displays a list of upcoming and past membership renewals.
 *
 * The widget is split into two sections:
 * - "Upcoming" view: Displays active memberships that are due for renewal in the next 7 days.
 * - "Past" view: Displays active memberships that have an end date in the past.
 */
export default function MembershipRenewalsWidget() {
  const { siteKeyDoc } = useSiteKeyDocStore();
  const { membershipTemplates: templates, loading: templatesLoading } =
    useMembershipTemplatesStore();

  const [viewMode, setViewMode] = useState<ViewMode>("upcoming");
  const [upcomingList, setUpcomingList] = useState<ExistingMembership[] | null>(
    null,
  );
  const [pastList, setPastList] = useState<ExistingMembership[] | null>(null);
  const [ungroupedRenewals, setUngroupedRenewals] = useState<
    RenewalItem[] | null
  >(null);
  const [renewals, setRenewals] = useState<GroupedRenewals>({});

  const isLoading =
    (viewMode === "upcoming" && upcomingList === null) ||
    (viewMode === "past" && pastList === null) ||
    ungroupedRenewals === null ||
    templatesLoading;

  // Fetch active memberships that are due for renewal in the next 7 days
  useEffect(() => {
    if (!siteKeyDoc) return undefined;
    const now = DateTime.now();
    // fetching yesterday through 6 days out, cus renewals happen the day after membershipEndDate
    const yesterday = now.startOf("day").minus({ days: 1 });
    const sixDaysLater = now.endOf("day").plus({ days: 6 });

    const unsubscribe = DbRead.memberships.subscribeByDateRange({
      siteKey: siteKeyDoc.id,
      status: MembershipStatus.Active,
      startDate: yesterday.toJSDate(),
      endDate: sixDaysLater.toJSDate(),
      onChange: setUpcomingList,
      onError: logger.error,
    });
    return () => unsubscribe && unsubscribe();
  }, [siteKeyDoc]);

  // Fetch active memberships with end dates in the past
  useEffect(() => {
    if (!siteKeyDoc) return undefined;
    const unsubscribe = DbRead.memberships.subscribeByDateRange({
      siteKey: siteKeyDoc.id,
      status: MembershipStatus.Active,
      endDate: DateTime.now().startOf("day").minus({ days: 2 }).toJSDate(),
      onChange: setPastList,
      onError: logger.error,
    });
    return () => unsubscribe && unsubscribe();
  }, [siteKeyDoc]);

  // Build renewal items after we have memberships
  useEffect(() => {
    async function buildRenewalItems() {
      if (!siteKeyDoc || templatesLoading) return;

      const memberships = viewMode === "upcoming" ? upcomingList : pastList;
      if (!memberships) return;

      const renewalItems: RenewalItem[] = [];

      for (const membership of memberships) {
        const template = templates.find(
          (t) => t.id === membership.membershipTemplateID,
        );
        if (!template) {
          logger.error(
            `Template (${membership.membershipTemplateID}) not found in templates list. ${membership.refPath}`,
          );
          continue;
        }

        let renewalTemplate: ExistingMembershipTemplate | null = null;
        if (template.renewalTemplateID) {
          const rt = templates.find((t) => t.id === template.renewalTemplateID);
          if (!rt) {
            logger.error(
              `Renewal template (${template.renewalTemplateID}) not found in templates list. ${template.refPath}`,
            );
            continue;
          }
          renewalTemplate = rt;
        }

        const results = await Promise.allSettled([
          DbRead.customers.get(siteKeyDoc.id, membership.customerID),
          getMembershipConfig({
            membership: membership,
            template: renewalTemplate ?? template,
            isRenewalTemplate: renewalTemplate !== null,
            defaultEmailDuesCollectedReceipt:
              siteKeyDoc.customizations
                .sendAutomatedReceiptToCustomersForMemberships === true,
          }),
        ]);

        let customer: ExistingCustomer;
        if (results[0].status === "fulfilled") {
          customer = results[0].value;
        } else {
          logger.error(
            `Error fetching customer (${membership.customerID}) for membership ${membership.refPath}`,
            results[0].reason,
          );
          continue;
        }

        let membershipConfig: MembershipConfig;
        if (results[1].status === "fulfilled") {
          membershipConfig = results[1].value;
        } else {
          logger.error(
            `${membership.refPath}: Error fetching membership config. Using fallback config.`,
            results[1].reason,
          );
          membershipConfig = getFallbackMembershipConfig({
            membership: membership,
            template: renewalTemplate ?? template,
            isRenewalTemplate: renewalTemplate !== null,
            defaultEmailDuesCollectedReceipt:
              siteKeyDoc.customizations
                .sendAutomatedReceiptToCustomersForMemberships === true,
          });
        }

        let customerName = customer.name;
        if (customer.type === "commercial") {
          if (customer.firstName || customer.lastName) customerName += " -";
          if (customer.firstName) customerName += ` ${customer.firstName}`;
          if (customer.lastName) customerName += ` ${customer.lastName}`;
        }

        let email: string | null = null;
        if (membership.overrideData?.email) {
          email = membership.overrideData.email;
        } else if (customer.email && customer.email.length > 0) {
          email = customer.email[0];
        }

        let phone: string | null = null;
        if (customer.phone && customer.phone.length > 0) {
          phone = phoneUtils.display(customer.phone[0]);
        }

        let customerContactDisplay = "";
        if (email && phone) {
          customerContactDisplay = `${phone} • ${email}`;
        } else if (email) {
          customerContactDisplay = email;
        } else if (phone) {
          customerContactDisplay = phone;
        }

        renewalItems.push({
          membershipConfig: membershipConfig,
          customerID: membership.customerID,
          customerName: customerName,
          customerContactDisplay: customerContactDisplay,
          templateTitle: template.title,
          endDate: membership.membershipEndDate!, // memberships were fetched by membershipEndDate, therefore it exists and we'll bang it out
        });
      }
      setUngroupedRenewals(renewalItems);
    }
    buildRenewalItems();
  }, [
    upcomingList,
    pastList,
    siteKeyDoc,
    templates,
    templatesLoading,
    viewMode,
  ]);

  // Group renewals by date
  useEffect(() => {
    if (!ungroupedRenewals) return;
    const grouped: GroupedRenewals = {};

    for (const item of ungroupedRenewals) {
      const date = item.endDate.toDate();
      const calDate = new CalendarDate(
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
      ).add({ days: 1 }); // add one day to the membershipEndDate to get the renewal date
      const dateKey = `${calDate.year}-${calDate.month.toString().padStart(2, "0")}-${calDate.day.toString().padStart(2, "0")}`;

      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(item);
    }
    setRenewals(grouped);
  }, [ungroupedRenewals]);

  // Format date for display
  function formatDateHeading(dateStr: string): string {
    const zoneIdentifier = getLocalTimeZone();
    const [year, month, day] = dateStr.split("-").map(Number);
    const calDate = new CalendarDate(year, month, day);

    const todayDate = today(zoneIdentifier);
    const tomorrowDate = todayDate.add({ days: 1 });
    const yesterdayDate = todayDate.subtract({ days: 1 });

    if (calDate.compare(todayDate) === 0) return "Today";
    if (calDate.compare(tomorrowDate) === 0) return "Tomorrow";
    if (calDate.compare(yesterdayDate) === 0) return "Yesterday";

    // Format as "Monday, January 1"
    const jsDate = calDate.toDate(zoneIdentifier);
    const formatter = new Intl.DateTimeFormat("en-US", {
      year: viewMode === "past" ? "numeric" : undefined,
      weekday: "long",
      month: "long",
      day: "numeric",
    });
    return formatter.format(jsDate);
  }

  function getSortedDateKeys(): string[] {
    return Object.keys(renewals).sort((a, b) => {
      const [yearA, monthA, dayA] = a.split("-").map(Number);
      const [yearB, monthB, dayB] = b.split("-").map(Number);

      const dateA = new CalendarDate(yearA, monthA, dayA);
      const dateB = new CalendarDate(yearB, monthB, dayB);

      // For past view, sort in reverse (most recent first)
      return viewMode === "upcoming"
        ? dateA.compare(dateB)
        : dateB.compare(dateA);
    });
  }

  function renderRenewalPrice(renewalPrice: number): string {
    if (isNaN(renewalPrice)) return "[unknown]";
    return currencyFormatter(
      renewalPrice,
      typeof siteKeyDoc?.customizations.accounting?.currency === "string"
        ? siteKeyDoc.customizations.accounting.currency
        : "USD",
    );
  }

  if (!siteKeyDoc) return null;

  const loadingComponent = (
    <div className="flex h-full items-center justify-center">
      <div className="relative flex h-8 w-8 items-center justify-center">
        <div className="absolute h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
      </div>
    </div>
  );

  // Helper function to render appropriate content based on state
  const renderContent = () => {
    if (isLoading) return loadingComponent;

    if (getSortedDateKeys().length === 0) {
      return (
        <div className="flex h-full items-center justify-center text-gray-500">
          {viewMode === "upcoming" ? (
            <p>No active memberships are due for renewal in the next 7 days.</p>
          ) : (
            <p>No active memberships have an end date that has passed.</p>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        {getSortedDateKeys().map((dateKey) => (
          <div key={dateKey} className="pb-2 last:pb-0">
            <h3 className="mb-2 text-lg font-normal text-gray-800">
              {formatDateHeading(dateKey)}
              {renewals[dateKey].length > 4 && (
                <span className="ml-2 inline-block text-base text-gray-500">
                  ({renewals[dateKey].length})
                </span>
              )}
            </h3>
            <div className="flex flex-col gap-2">
              {renewals[dateKey].map((renewal) => (
                <RenewalCard
                  key={renewal.membershipConfig.id}
                  renewal={renewal}
                  displayPrice={renderRenewalPrice(
                    renewal.membershipConfig.isRenewalTemplate
                      ? renewal.membershipConfig.price
                      : renewal.membershipConfig.renewalPrice,
                  )}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "upcoming" ? "past" : "upcoming");
    setUngroupedRenewals(null);
    setRenewals({});
  };

  return (
    <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-white shadow-md">
      {/* Header */}
      <div
        className={`rounded-t-lg border-b border-blue-800 bg-blue-600 text-white ${
          viewMode === "past" ? "px-4 pb-2 pt-4" : "p-4"
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h2 className="text-base font-normal xs:text-lg">
              {viewMode === "upcoming" ? "Upcoming" : "Past"} Membership
              Renewals
            </h2>

            {/* display the total count */}
            {viewMode === "upcoming" && (
              <>
                {upcomingList == null ? (
                  <div className="relative flex h-4 w-4 items-center justify-center">
                    <div className="absolute h-4 w-4 animate-spin rounded-full border-2 border-gray-100 border-t-transparent"></div>
                  </div>
                ) : (
                  <span
                    className={`rounded-full bg-white/60 ${
                      upcomingList.length < 9 ? "px-1 py-0.5" : "p-1"
                    }`}
                  >
                    <p className="text-sm leading-none text-blue-700">
                      {upcomingList.length}
                    </p>
                  </span>
                )}
              </>
            )}
            {viewMode === "past" && (
              <>
                {pastList === null ? (
                  <div className="relative flex h-4 w-4 items-center justify-center">
                    <div className="absolute h-4 w-4 animate-spin rounded-full border-2 border-gray-100 border-t-transparent"></div>
                  </div>
                ) : (
                  <span
                    className={`rounded-full bg-white/60 ${
                      pastList.length < 9 ? "px-1 py-0.5" : "p-1"
                    }`}
                  >
                    <p className="text-sm leading-none text-blue-700">
                      {pastList.length}
                    </p>
                  </span>
                )}
              </>
            )}
          </div>
          <button
            onClick={toggleViewMode}
            className="flex items-center rounded-lg bg-white/10 px-3 py-1 text-sm font-medium transition-colors hover:bg-white/20"
          >
            {viewMode === "upcoming" ? "Past" : "Upcoming"}
          </button>
        </div>
        {viewMode === "past" && (
          <p className="mt-2 text-sm text-gray-100">
            These are active memberships that may have slipped through the
            cracks.
          </p>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-3 pt-2">
        {renderContent()}
      </div>
    </div>
  );
}

function RenewalCard(props: {
  renewal: RenewalItem;
  displayPrice: string;
}): JSX.Element {
  const { renewal, displayPrice } = props;
  return (
    <StyledTooltip title={getTooltip(renewal.membershipConfig)}>
      <div
        key={renewal.membershipConfig.id}
        className="flex flex-col justify-between overflow-hidden rounded-md border border-gray-200 pb-0.5 pl-2 pr-2 pt-1 text-base text-gray-900 xs:flex-row xs:items-center xs:gap-2 xs:pl-1 xs:pt-0.5"
      >
        <div className="flex">
          <div className="flex items-center gap-2 xs:flex-col xs:gap-1">
            <StyledTooltip title="View Customer">
              <ButtonViewCustomer
                customerID={renewal.customerID}
                overrideButtonClasses="rounded-full text-blue-800 bg-blue-100 transition-colors hover:bg-blue-200"
                overrideIconStyles={{
                  padding: "4px",
                  height: "28px",
                  width: "28px",
                }}
              />
            </StyledTooltip>
            <StyledTooltip title="View Membership">
              <ButtonViewMembership
                membershipID={renewal.membershipConfig.id}
                overrideButtonClasses="rounded-full text-blue-800 bg-blue-100 transition-colors hover:bg-blue-200"
                overrideIconStyles={{
                  marginBottom: "-1px",
                  marginTop: "1px",
                  padding: "4px",
                  height: "28px",
                  width: "28px",
                }}
              />
            </StyledTooltip>
          </div>
          <span className="ml-1 hidden w-1 border-l border-dashed border-gray-200 xs:block"></span>
        </div>

        <div className="mr-auto">
          <h4 className="font-normal">{renewal.customerName}</h4>
          <p className="text-sm text-gray-500">
            {renewal.customerContactDisplay}
          </p>
        </div>

        <div className="flex flex-col items-end text-right">
          <span
            className={`text-xs ${
              renewal.membershipConfig.willAutoRenew
                ? "text-green-600"
                : "text-orange-600"
            }`}
          >
            {renewal.membershipConfig.willAutoRenew
              ? "Auto-renews"
              : "Manual renewal"}
          </span>
          <span className="font-medium">{displayPrice}</span>
          {/* find that arrow thing → */}
          <p className="text-sm text-gray-400">
            {renewal.templateTitle}
            {renewal.membershipConfig.isRenewalTemplate && (
              <span> → {renewal.membershipConfig.templateTitle}</span>
            )}
          </p>
        </div>
      </div>
    </StyledTooltip>
  );
}

/** if it's not an auto renewal, returns empty string (aka no tooltip) */
function getTooltip(config: MembershipConfig): string {
  if (!config.willAutoRenew) return "";
  let tooltip = "";
  if (config.isRenewalTemplate) {
    tooltip += `This membership will renew to ${config.templateTitle}.`;
  }

  if (config.automaticallySendInvoice) {
    tooltip += " Invoice will be emailed.";
  } else {
    tooltip += " Invoice will not be emailed.";
  }

  if (config.automaticallyPayInvoice) {
    tooltip += " Payment will be automatically collected;";
    if (config.automaticallySendReceipt) {
      tooltip += " receipt will be emailed.";
    } else {
      tooltip += " receipt will not be emailed.";
    }
  } else {
    tooltip += " Payment will not be automatically collected.";
  }
  return tooltip;
}
